import Layout from "../components/Layout";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { HelpCircle, UserRound, Truck } from "lucide-react";

// Task 7 — 10 canned FAQs split into two sections. Copy is deliberately
// consistent with the rest of the app (£500 protection, licensed carriers,
// duty-of-care receipt, price variation flow) so answers don't contradict
// the live product.
const CUSTOMER_FAQS = [
  {
    q: "How does Binshare work?",
    a: "Snap a photo of your waste, describe the job in 30 seconds and drop a pin. Licensed UK waste carriers see your listing and submit competitive quotes — you accept the one that works for you. Payment is held safely in escrow and only released to the carrier once the job is done.",
  },
  {
    q: "Are your carriers licensed?",
    a: "Yes. Every carrier on Binshare must upload a valid Environment Agency (or NRW in Wales) waste-carrier licence, proof of Public Liability insurance and their disposal-facility details before they can bid on a job. Our team reviews the paperwork manually and rejects anything that doesn't check out.",
  },
  {
    q: "What is the £500 Flytipping Guarantee?",
    a: "If your waste is illegally disposed of via a Binshare job — for example, fly-tipped — we'll cover the resulting clean-up and enforcement costs up to £500. It's our commitment that using a Binshare carrier is safer than picking one at random online, and it's automatic on every completed job.",
  },
  {
    q: "What happens after I post a job?",
    a: "Your job appears instantly in the marketplace and nearby licensed carriers get an alert. Most posts receive their first quote within a couple of hours. You'll get an in-app notification for every new bid — accept one, pay through Stripe, and the carrier will confirm the pickup time with you in chat.",
  },
  {
    q: "Can I cancel a job?",
    a: "Yes. If you cancel at least 24 hours before the agreed pickup, you get a full refund. Cancelling inside 24 hours means the carrier keeps the payment as compensation for the reserved slot. To cancel, open the job in your dashboard and tap \"Cancel job\".",
  },
];

const CARRIER_FAQS = [
  {
    q: "How do I get jobs?",
    a: "After you finish onboarding — waste-carrier licence, Public Liability insurance, disposal facility and vehicle types — you'll see every open job in your service area. Submit a bid with your price and ETA; if the customer accepts, the money is held in escrow and the pickup is booked.",
  },
  {
    q: "What documents do I need to register?",
    a: "You'll need a valid Environment Agency (or NRW) waste-carrier licence, a Public Liability insurance certificate covering waste removal, and the name plus site-permit number of the facility where your waste is legally disposed. Add at least one vehicle type (Car / Small van / Large van / Luton / Flatbed / HGV) so we can match you to the right jobs.",
  },
  {
    q: "How do I handle a job where waste is more than described?",
    a: "Open the job and tap \"Request Price Variation\". Snap a photo of what you found, add a short reason and propose a revised price — the customer gets a notification and can Accept or Decline. Accepting updates the price and top-up payment is collected through Stripe; declining preserves the original price and the record stays visible to both parties.",
  },
  {
    q: "When do I get paid?",
    a: "Funds clear to your Stripe Connect account 24 hours after you mark the job complete and the customer confirms — this window is our dispute pause. From Stripe it hits your bank on their standard payout schedule (usually 1–2 working days in the UK).",
  },
  {
    q: "What is a duty-of-care receipt?",
    a: "It's a legal document that proves the waste you collected was disposed of at a licensed facility. Binshare generates one automatically at the end of every completed job, using the disposal-facility name and permit number from your profile. Both you and the customer keep a copy — it's your evidence if anyone ever queries where the waste ended up.",
  },
];

function FaqBlock({ id, title, icon: Icon, items }) {
  return (
    <Card
      data-testid={`faq-section-${id}`}
      className="rounded-2xl border-[#E2DFD8] bg-white p-6 sm:p-8"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#1B3624] text-white flex items-center justify-center">
          <Icon className="w-5 h-5" />
        </div>
        <h2 className="text-xl sm:text-2xl tracking-tight font-medium" style={{ fontFamily: "Outfit" }}>
          {title}
        </h2>
      </div>
      <Accordion type="single" collapsible className="mt-4">
        {items.map((item, idx) => (
          <AccordionItem key={idx} value={`${id}-${idx}`} className="border-[#F0EEE8]">
            <AccordionTrigger
              data-testid={`faq-q-${id}-${idx}`}
              className="text-left text-[15px] font-medium text-[#1B3624] hover:no-underline"
              style={{ fontFamily: "Outfit" }}
            >
              {item.q}
            </AccordionTrigger>
            <AccordionContent
              data-testid={`faq-a-${id}-${idx}`}
              className="text-[14px] leading-relaxed text-[#4A4842]"
            >
              {item.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </Card>
  );
}

export default function FAQ() {
  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12">
        <div className="flex items-center gap-2">
          <Badge className="bg-[#EBE8E0] text-[#1B3624] hover:bg-[#EBE8E0] uppercase text-[10px] tracking-wide">
            <HelpCircle className="w-3 h-3 mr-1" /> Help centre
          </Badge>
        </div>
        <h1
          data-testid="faq-title"
          className="mt-3 text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium"
          style={{ fontFamily: "Outfit" }}
        >
          Frequently asked questions
        </h1>
        <p className="mt-2 text-[15px] text-[#6B6965] leading-relaxed">
          Quick answers to the things customers and carriers ask most often. Still stuck?{" "}
          <a href="mailto:support@binshare.co.uk" className="text-[#4A6B56] underline">
            support@binshare.co.uk
          </a>
          .
        </p>

        <div className="mt-8 space-y-5">
          <FaqBlock id="customers" title="For customers" icon={UserRound} items={CUSTOMER_FAQS} />
          <FaqBlock id="carriers" title="For carriers" icon={Truck} items={CARRIER_FAQS} />
        </div>
      </div>
    </Layout>
  );
}
